import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

/**
 * Ô tìm người chơi theo tên nhân vật / tên tài khoản, dùng chung cho các trang có mục "Tra người chơi".
 * Có quyền xem người chơi thì tìm theo tên (/api/admin/players?q=); không có thì chỉ nhận mã tài khoản (số).
 * Khớp đúng một người thì gọi `onPick` luôn; khớp nhiều thì hiện chip cho admin chọn.
 * `onPick({ userId, name, username, online })` — name / username có thể null khi tìm bằng số.
 */
function PlayerSearch({ onPick, buttonText, autoFocus }) {
  const canSearch = useAuth().can(PERMISSIONS.PLAYER_VIEW);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState(null);
  const [run, busy] = useRunner();

  const pick = (person) => {
    setMatches(null);
    onPick(person);
  };

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    if (!canSearch) {
      if (/^\d+$/.test(q)) pick({ userId: Number(q), name: null, username: null });
      else setMatches([]);
      return;
    }
    const { ok, result } = await run(() => api.get("/api/admin/players", { q, size: 10 }));
    if (!ok) return;
    const rows = result?.items || result || [];
    const lower = q.toLowerCase();
    const exact = rows.filter(
      (p) =>
        (p.name || "").toLowerCase() === lower ||
        (p.username || "").toLowerCase() === lower ||
        String(p.userId) === q
    );
    if (exact.length === 1) pick(exact[0]);
    else if (rows.length === 1) pick(rows[0]);
    else setMatches(rows);
  };

  return (
    <MDBox>
      {/* Không dùng <form>: ô này hay nằm trong hộp thoại (cũng là form) — Enter ở đây chỉ tìm,
          không được bấm nút chính của hộp thoại. */}
      <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <MDInput
          size="small"
          autoFocus={autoFocus}
          label={canSearch ? "Tên nhân vật hoặc tên tài khoản" : "Mã tài khoản (số)"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              search();
            }
          }}
          sx={{ minWidth: 260 }}
        />
        <MDButton size="small" color="info" disabled={busy || !query.trim()} onClick={search}>
          <Icon>search</Icon>&nbsp;{buttonText}
        </MDButton>
      </MDBox>
      {matches && matches.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Không tìm thấy ai khớp &quot;{query.trim()}&quot;
          {!canSearch && " — tài khoản của bạn chỉ tra được bằng mã tài khoản (số)"}.
        </Alert>
      )}
      {matches && matches.length > 1 && (
        <MDBox mt={2}>
          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
            Có {matches.length} người khớp, chọn đúng người:
          </MDTypography>
          <MDBox display="flex" gap={0.5} flexWrap="wrap">
            {matches.map((p) => (
              <Chip
                key={p.userId}
                label={`${p.name || "(chưa tạo nhân vật)"} · ${p.username || p.userId}${
                  p.online ? " · đang online" : ""
                }`}
                color={p.online ? "success" : "default"}
                onClick={() => pick(p)}
              />
            ))}
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
}

PlayerSearch.propTypes = {
  onPick: PropTypes.func.isRequired,
  buttonText: PropTypes.string,
  autoFocus: PropTypes.bool,
};

PlayerSearch.defaultProps = { buttonText: "Xem", autoFocus: false };

export default PlayerSearch;
